import React, { useCallback } from 'react';
import date from 'date-and-time';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { platformName } from '../platformMeta';
import { PostFailureNotice } from './PostFailureNotice';
import { PostRescheduleForm } from './PostRescheduleForm';
import { failedPostNeedsComposer } from './postFailMessages';
import type { PostStateActionsProps } from './dayPanelTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "Try again now" sends the post one minute from now. */
const RETRY_DELAY_MINUTES = 1;

/**
 * A failed post (design b2): why it failed, then the fix. A lost sign-in first asks to connect
 * the channel again. A post that cannot go out as it is goes to the Composer and gets no new time.
 * Each other failed post gets "Try again now" or a new time (no new scheduled post).
 */
const FailedActionsComponent: React.FC<PostStateActionsProps> = ({
  post,
  actions,
  onEditInComposer,
  onOpenChannels,
  onAskDelete,
}) => {
  const intl = useIntl();
  const needsComposer = failedPostNeedsComposer(post);
  const needsLogin = post.failCode === 'loginRefreshFailed' || post.failCode === 'tiktokReconnect';

  const reschedule = useCallback(
    (scheduledAt: string) => {
      actions.reschedule(post.uuid, scheduledAt).catch(console.error);
    },
    [actions, post.uuid]
  );
  const retryNow = useCallback(
    () => reschedule(date.addMinutes(new Date(), RETRY_DELAY_MINUTES).toISOString()),
    [reschedule]
  );
  const editLabel = needsComposer
    ? intl.formatMessage({ id: 'app.sml.promote.calendar.failed.fix', defaultMessage: 'Fix in Composer' })
    : intl.formatMessage({ id: 'app.sml.promote.calendar.editInComposer', defaultMessage: 'Edit in Composer' });

  return (
    <>
      <div className={styles.dpBody}>
        <PostFailureNotice post={post} />
        {needsComposer ? null : <PostRescheduleForm isSaving={actions.isWriting} onReschedule={reschedule} />}
      </div>
      <div className={styles.dpActs}>
        {needsLogin ? (
          <SMLButton color="primary" omitCap stretch onClick={onOpenChannels}>
            {intl.formatMessage(
              { id: 'app.sml.promote.calendar.failed.connect', defaultMessage: 'Connect {platform}' },
              { platform: platformName(post.platform) }
            )}
          </SMLButton>
        ) : null}
        {needsComposer ? (
          <SMLButton color="primary" omitCap stretch onClick={() => onEditInComposer(post)}>
            {editLabel}
          </SMLButton>
        ) : (
          <>
            <SMLButton
              size="sm"
              color={needsLogin ? undefined : 'primary'}
              outline={needsLogin}
              omitCap
              stretch
              disabled={actions.isWriting}
              onClick={retryNow}
            >
              {intl.formatMessage({ id: 'app.sml.promote.calendar.failed.retry', defaultMessage: 'Try again now' })}
            </SMLButton>
            <SMLButton size="sm" outline omitCap stretch onClick={() => onEditInComposer(post)}>
              {editLabel}
            </SMLButton>
          </>
        )}
        <button type="button" className={styles.linkbtn} onClick={() => onAskDelete(post)}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.delete', defaultMessage: 'Delete this post' })}
        </button>
      </div>
    </>
  );
};

export const FailedActions = TypedMemo(FailedActionsComponent);
